"use client";

import { useEmailValidation } from "@/components/hooks/useEmailValidation";
import { useUser } from "@/context/UserContext";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import {
  fetchTiposIdentificacion,
  mapApiErrorToMessage,
  resetPasswordRequest,
} from "../Services/resetPasswordService";
import type { TipoIdentificacion } from "../../Types/types";

export const useResetPassword = () => {
  const [email, setEmail] = useState("");
  const { emailError, handleEmailChange } = useEmailValidation();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [numeroDocumentoError, setNumeroDocumentoError] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiErrorUser, setApiErrorUser] = useState<string | null>(null);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successData, setSuccessData] = useState<{ title: string; message: string } | null>(null);

  const { isLoadingUser, user } = useUser();
  const router = useRouter();

  const [tiposIdentificacion, setTiposIdentificacion] = useState<TipoIdentificacion[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(true);

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    handleEmailChange(value);
  };

  const handleTipoDocumentoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTipoDocumento(e.target.value);
    if (numeroDocumentoError) setNumeroDocumentoError("");
  };

  const handleNumeroDocumentoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNumeroDocumento(value);

    if (!/^\d*$/.test(value)) {
      setNumeroDocumentoError("El número de documento solo puede contener dígitos");
    } else {
      setNumeroDocumentoError("");
    }
  };

  const handleAcceptTermsChange = (checked: boolean) => {
    setAcceptTerms(checked);
  };

  const handleCloseErrorAlert = () => {
    setShowErrorAlert(false);
    setApiErrorUser(null);
  };

  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(false);
    setSuccessData(null);
  };

  const areFieldsValid =
    tipoDocumento.trim() !== "" &&
    numeroDocumento.trim() !== "" &&
    !numeroDocumentoError &&
    email.trim() !== "" &&
    !emailError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isEmailValid = handleEmailChange(email);

    if (
      !tipoDocumento ||
      !numeroDocumento ||
      numeroDocumentoError ||
      !isEmailValid ||
      !acceptTerms
    ) {
      return;
    }

    try {
      setLoading(true);
      setApiErrorUser(null);

      const data = await resetPasswordRequest({
        email,
        num_id: numeroDocumento,
        tipo_id: tipoDocumento,
      });

      if (data.success) {
        setSuccessData({
          title: "¡Correo enviado!",
          message:
            data.message ||
            `Se ha enviado un correo de recuperación a ${email}. Revisa tu bandeja de entrada y sigue las instrucciones.`,
        });
        setShowSuccessAlert(true);

        setTimeout(() => {
          router.push("/login");
        }, 3000);
      } else {
        const friendly = mapApiErrorToMessage(undefined, data);
        setApiErrorUser(friendly);
      }
    } catch (err) {
      if (typeof err === "object" && err !== null && "isAxiosError" in err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const axiosError = err as any;
        if (axiosError.isAxiosError) {
          const status = axiosError.response?.status;
          const payload = axiosError.response?.data ?? axiosError.message ?? String(err);
          const friendly = mapApiErrorToMessage(status, payload);
          setApiErrorUser(friendly);
          return;
        }
      }

      console.error("Non-axios error on reset password:", err);
      setApiErrorUser("Error del servidor. Intenta de nuevo más tarde.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchTipos = async () => {
      try {
        setLoadingTipos(true);
        const tipos = await fetchTiposIdentificacion();
        setTiposIdentificacion(tipos);
      } catch (error) {
        console.error("Error cargando tipos de identificación:", error);
      } finally {
        setLoadingTipos(false);
      }
    };

    fetchTipos();
  }, []);

  useEffect(() => {
    if (apiErrorUser) {
      setShowErrorAlert(true);
    }
  }, [apiErrorUser]);

  useEffect(() => {
    if (!isLoadingUser && user?.token) {
      router.push("/login");
    }
  }, [isLoadingUser, user, router]);

  return {
    email,
    emailError,
    onEmailChange: handleEmailInputChange,
    onSubmit: handleSubmit,
    acceptTerms,
    onAcceptTermsChange: handleAcceptTermsChange,
    isCheckboxDisabled: !areFieldsValid,
    tipoDocumento,
    numeroDocumento,
    numeroDocumentoError,
    onTipoDocumentoChange: handleTipoDocumentoChange,
    onNumeroDocumentoChange: handleNumeroDocumentoChange,
    loading,
    tiposIdentificacion,
    loadingTipos,
    apiErrorUser,
    showErrorAlert,
    showSuccessAlert,
    successData,
    onCloseErrorAlert: handleCloseErrorAlert,
    onCloseSuccessAlert: handleCloseSuccessAlert,
  };
};
